import React from 'react';

import { useTranslation } from 'react-i18next';

import { Button, Icon, PopupMenu } from 'design-system-web';

import { Flex } from 'components';
import { BreadCrumbs } from 'components/lds';
import { H3 } from 'components/typography';

import { ClientTask } from 'graphql-query-contracts';

import AssignedMemberDropdown from 'pages/portal/admin/channel/service-requests/details/components/AssignedMemberDropdown';
import { StaffTeamsMultiSelectDropdown } from 'pages/portal/admin/channel/service-requests/details/components/StaffTeamsMultiSelectDropdown';

import { PMTaskStatusDropdown } from '../../../components/TaskStatusDropDown/PMTaskStatusDropdown';
import { buildTaskActions } from '../../../hooks/buildTaskActions';
import { TaskDetailsViewModel } from '../../../hooks/buildTaskDetailsViewModel';
import { convertTask } from '../../../hooks/convertTask';
import { usePmTaskAssignment } from '../../../hooks/usePmTaskAssignment';
import styles from '../index.scss';

type TaskDetailsHeaderProps = {
  task: ClientTask;
  viewModel: TaskDetailsViewModel;
  assignment: ReturnType<typeof usePmTaskAssignment>;
  actions: ReturnType<typeof buildTaskActions>;
  canEditStatus: boolean;
  canAssignTeam: boolean;
  canAssignIndividual: boolean;
  canBeAssigned: boolean;
  loading: boolean;
  auditLogLoading: boolean;
  onStatusChanged: () => void;
};

export function TaskDetailsHeader({
  task,
  viewModel,
  assignment,
  actions,
  canEditStatus,
  canAssignTeam,
  canAssignIndividual,
  canBeAssigned,
  loading,
  auditLogLoading,
  onStatusChanged,
}: TaskDetailsHeaderProps) {
  const { t } = useTranslation();
  const {
    assignedMember,
    assignedTeams,
    assignableTeams,
    assigneeOptions,
    updateAssignee,
    updateAssigneeGroup,
  } = assignment;

  return (
    <>
      <BreadCrumbs
        links={[
          {
            label: t`web.admin.workOrder.preventiveMaintenance.title`,
            url: viewModel.taskListPath,
          },
          {
            label: t`web.admin.workOrder.preventiveMaintenance.taskTabHeading`,
            url: viewModel.taskListPath,
          },
          {
            label: `${viewModel.userFriendlyId}`,
          },
        ]}
      />
      <Flex
        className={styles.TaskDetailsHeader}
        direction="row"
        justify="space-between"
        align="flex-start"
      >
        <Flex direction="column" gap={4}>
          <H3>{viewModel.userFriendlyId}</H3>
          <span>
            {t(
              'web.admin.workOrder.preventiveMaintenance.task.details.createdBy',
              {
                date: viewModel.createdOn,
                name: viewModel.creatorName,
              }
            )}
          </span>
          <Flex gap={5}>
            <PMTaskStatusDropdown
              taskData={convertTask(task)}
              isTaskList={false}
              refetch={onStatusChanged}
              pillSize="md"
              disableEdit={!canEditStatus}
            />
            <StaffTeamsMultiSelectDropdown
              testId="teamAssigneeDropdown"
              items={assignableTeams}
              disabled={!canAssignTeam}
              isSearchable
              onChange={assigneeTeam => updateAssigneeGroup(assigneeTeam)}
              value={assignedTeams as any}
              placeholder={t(
                'web.admin.workOrder.preventiveMaintenance.task.details.assignedToTeam.select'
              )}
              label={t`web.admin.workOrder.preventiveMaintenance.task.details.assignedToTeam`}
              doTranslate={false}
            />
            <AssignedMemberDropdown
              testId="assigneeDropdown"
              fieldName="assign-to-person"
              users={assigneeOptions}
              setAssignedMember={updateAssignee}
              disabled={!(canAssignIndividual || canBeAssigned)}
              assignedMember={assignedMember}
              placeholder={t`web.admin.workOrder.preventiveMaintenance.task.details.assignedTo.select`}
              label={t`web.admin.workOrder.preventiveMaintenance.task.details.assignedTo`}
              className={styles.SelectDropdown}
              doTranslate={false}
            />
          </Flex>
        </Flex>
        <Flex align="center" gap={4}>
          <Flex m={2}>
            <PopupMenu
              trigger={
                <Button
                  variant="secondary"
                  endIcon={<Icon name="chevron-down" />}
                  testId="actionsButton"
                  loading={loading || auditLogLoading}
                >
                  {t('web.admin.serviceRequest.equipment.action.title')}
                </Button>
              }
              items={actions}
            />
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}
